import React from "react";
import _ from "lodash";
import {
  PencilLineOutlined,
  RefreshArrowOutlined,
  ArchiveOutlined,
  DeleteOutlined,
  LanguageOutlined,
  SyncPendingOutlined,
  CopyOutlined,
} from "@toddle-edu/ds-icons";

import { getCurrentPathname } from "services/NavigationService";
import ACLStore from "lib/aclStore";
import { store } from "store/createStore";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getMemoizedLangFunction } from "Utils";
import { getSettingValue } from "Utils/ConfigurationUtils";
import { MERGE_CUSTOM_DATASETS_INTO_LEARNING_GOALS } from "Constants/permissionConstants";

import { DATA_SET_MODES } from "DatasetDetails/modules/Constants";

import {
  CURRICULUM_PROGRAM_TO_DATASET_GROUPS_MAP,
  DATASETS_GROUP_TYPES,
  FEEDLIST_COLUMN_IDS,
  FEEDLIST_COLUMN_LIST,
  PLANNER_ELEMENT_NODE,
  PLANNER_INTERFACE_TYPES,
  STATUS_OPTIONS,
  FEEDLIST_COLUMN_LIST_BY_GRADING_TYPE,
  FEEDLIST_COLUMN_LIST_CONFIG,
  STATUS_FILTERS,
  DATASET_ACTIONS,
  ACADEMIC_SETUP_TYPES,
  DATASETS_FILTERS_SETTING,
  ATL_PLANNER_ELEMENT_SET_TYPES,
} from "./constants";
import {
  CUSTOM_PLANNER_ELEMENT_SET_TYPES,
  DATASET_TYPE_FILTERS,
  getIsCustomPlannerElementSetType,
  getUiTypeLabels,
} from "./MergedDatasetsUtils";

const EMPTY_OBJ = {};

export const getStatusTooltipLabelFromKey = ({ value, t }) => {
  const option = _.find(STATUS_OPTIONS(t), { value });

  return option?.tooltipLabel || option.label;
};

export const getCurrentDatasetsGroupType = () => {
  const pathname = getCurrentPathname();
  let datasetsGroupType = DATASETS_GROUP_TYPES.DATASETS;
  if (
    _.includes(pathname, "standards") ||
    _.includes(pathname, "scopeandsequence") ||
    _.includes(pathname, "scales-and-descriptors") ||
    _.includes(pathname, "standard-calculation")
  )
    datasetsGroupType = DATASETS_GROUP_TYPES.STANDARDS;
  return datasetsGroupType;
};

export const getPlannerElementSetFromState = state => {
  const { type: currentCurriculumProgramType, plannerElementSets } = _.get(
    state,
    "platform.currentCurriculumProgram",
    {}
  );
  const { currentPlannerElementSets = {} } = _.get(state, "platform", {});

  const datasetsGroupType = getCurrentDatasetsGroupType();
  const datasetGroups =
    CURRICULUM_PROGRAM_TO_DATASET_GROUPS_MAP[currentCurriculumProgramType];
  const plannerElementSetType = datasetGroups?.[datasetsGroupType][0];

  const plannerElementSet =
    currentPlannerElementSets?.[plannerElementSetType] ||
    _.find(plannerElementSets, { type: plannerElementSetType });
  return plannerElementSet || EMPTY_OBJ;
};

export const getSetIdsAndPlannerElementSetIdsMapFromState = state => {
  const datasetsGroupType = getCurrentDatasetsGroupType();

  const { type: currentCurriculumProgramType, plannerElementSets } = _.get(
    state,
    "platform.currentCurriculumProgram",
    {}
  );
  const { currentPlannerElementSets = {} } = _.get(state, "platform", {});

  const datasetGroups =
    CURRICULUM_PROGRAM_TO_DATASET_GROUPS_MAP[currentCurriculumProgramType];
  const datasetTypes = datasetGroups?.[datasetsGroupType];
  const { setIds, setIdPlannerElementSetMap } = _.reduce(
    datasetTypes,
    (acc, type) => {
      const plannerElementSet =
        currentPlannerElementSets?.[type] ||
        _.find(plannerElementSets, { type });
      if (!plannerElementSet) return acc;
      const { id } = plannerElementSet;
      acc.setIds.push(id);
      acc.setIdPlannerElementSetMap[id] = plannerElementSet;
      return acc;
    },
    {
      setIds: [],
      setIdPlannerElementSetMap: {},
    }
  );
  return {
    setIds,
    setIdPlannerElementSetMap,
  };
};

export const getPlannerInterfaceType = ({ setType }) => {
  const nodeInterfaceType = _.has(PLANNER_INTERFACE_TYPES, setType)
    ? PLANNER_INTERFACE_TYPES[setType]
    : PLANNER_ELEMENT_NODE;
  return { nodeInterfaceType };
};

export const getCanRenameDataset = ({
  academicSetupType,
  datasetsGroupType,
  plannerElementType,
}) => {
  return (
    (academicSetupType === ACADEMIC_SETUP_TYPES.COURSE_BASED_GRADING &&
      datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS) ||
    (datasetsGroupType === DATASETS_GROUP_TYPES.DATASETS &&
      (_.includes(CUSTOM_PLANNER_ELEMENT_SET_TYPES, plannerElementType) ||
        ACLStore.can("FeatureFlag:EnableDatasetSetupTab")))
  );
};

export const getCanDeleteDataset = ({
  academicSetupType,
  datasetsGroupType,
  plannerElementType,
}) => {
  return (
    (academicSetupType === ACADEMIC_SETUP_TYPES.COURSE_BASED_GRADING &&
      datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS) ||
    (datasetsGroupType === DATASETS_GROUP_TYPES.DATASETS &&
      _.includes(CUSTOM_PLANNER_ELEMENT_SET_TYPES, plannerElementType))
  );
};
export const getActionsOptions = ({
  t,
  taggedInCoursesCount,
  taggedInTemplateFieldsCount = 0,
}) => [
  {
    key: DATASET_ACTIONS.RENAME,
    label: t("common:rename"),
    icon: <PencilLineOutlined />,
    getIsVisible: ({
      academicSetupType,
      datasetsGroupType,
      plannerElementType,
      source = "FEEDLIST",
    }) =>
      source === "FEEDLIST" &&
      getCanRenameDataset({
        academicSetupType,
        datasetsGroupType,
        plannerElementType,
      }),
  },
  {
    key: DATASET_ACTIONS.SYNC,
    label: t("common:sync"),
    icon: <SyncPendingOutlined />,
    getIsVisible: ({ plannerElementType, isPrimaryCurriculum }) =>
      isPrimaryCurriculum &&
      _.includes(ATL_PLANNER_ELEMENT_SET_TYPES, plannerElementType) &&
      ACLStore.can(MERGE_CUSTOM_DATASETS_INTO_LEARNING_GOALS),
  },
  {
    key: DATASET_ACTIONS.DUPLICATE,
    label: t("common:duplicate"),
    icon: <CopyOutlined />,
    getIsVisible: ({
      source = "FEEDLIST",
      datasetsGroupType,
      plannerElementType,
    }) =>
      source === "FEEDLIST" &&
      datasetsGroupType === DATASETS_GROUP_TYPES.DATASETS &&
      ACLStore.can(MERGE_CUSTOM_DATASETS_INTO_LEARNING_GOALS) &&
      !_.includes(CUSTOM_PLANNER_ELEMENT_SET_TYPES, plannerElementType),
  },
  {
    key: DATASET_ACTIONS.TRANSLATE,
    label: t("common:manage_with_label", {
      label: t("scopeAndSequence:translation"),
    }),
    icon: <LanguageOutlined />,
    getIsVisible: ({
      mode = DATA_SET_MODES.VIEW,
      availableLanguageOptions,
      draftCount,
    }) =>
      mode === DATA_SET_MODES.EDIT &&
      _.size(availableLanguageOptions) > 0 &&
      draftCount &&
      ACLStore.can("FeatureFlag:EnableDatasetTranslations"),
  },
  {
    key: DATASET_ACTIONS.RESTORE,
    label: t("common:restore"),
    icon: <RefreshArrowOutlined />,
    getIsVisible: ({ academicSetupType, datasetsGroupType, isArchived }) =>
      academicSetupType === ACADEMIC_SETUP_TYPES.COURSE_BASED_GRADING &&
      datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS &&
      isArchived,
  },
  {
    key: DATASET_ACTIONS.ARCHIVE,
    label: t("common:archive"),
    icon: <ArchiveOutlined />,
    getIsVisible: ({ academicSetupType, datasetsGroupType, isArchived }) =>
      academicSetupType === ACADEMIC_SETUP_TYPES.COURSE_BASED_GRADING &&
      datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS &&
      !isArchived,
  },
  {
    key: DATASET_ACTIONS.DELETE,
    label: t("common:delete"),
    icon: <DeleteOutlined />,
    isDestructive: true,
    disabled: taggedInCoursesCount > 0 || taggedInTemplateFieldsCount > 0,
    disabledTooltip:
      taggedInTemplateFieldsCount > 0
        ? t(
            "scopeAndSequence:dataset_delete_disabled_tooltip_template_fields",
            {
              count: taggedInTemplateFieldsCount,
            }
          )
        : t("scopeAndSequence:standard_set_delete_disabled_tooltip", {
            count: taggedInCoursesCount || 0,
          }),
    tooltipPlacement: "left",
    getIsVisible: ({
      academicSetupType,
      datasetsGroupType,
      plannerElementType,
      source = "FEEDLIST",
    }) =>
      getCanDeleteDataset({
        academicSetupType,
        datasetsGroupType,
        plannerElementType,
      }) &&
      (source === "FEEDLIST" ||
        (taggedInCoursesCount === 0 && taggedInTemplateFieldsCount === 0)),
  },
];

const getCurrentRowActionOptions = ({
  t,
  taggedInCoursesCount,
  taggedInTemplateFieldsCount,
  isArchived,
  academicSetupType,
  plannerElementType,
  isPrimaryCurriculum,
}) => {
  const actionOptions = getActionsOptions({
    t,
    taggedInCoursesCount,
    taggedInTemplateFieldsCount,
  });
  const datasetsGroupType = getCurrentDatasetsGroupType();
  return _.filter(actionOptions, ({ getIsVisible }) =>
    getIsVisible({
      academicSetupType,
      datasetsGroupType,
      isArchived,
      plannerElementType,
      isPrimaryCurriculum,
    })
  );
};

const getRowFeedData = ({
  t,
  nodes = [],
  currentColumns = [],
  academicSetupType,
  isPrimaryCurriculum,
}) => {
  return _.map(nodes, (node = {}) => {
    const {
      id,
      title,
      updatedAt,
      updatedBy,
      isArchived = false,
      subject,
      plannerElementNodeSetWrapperV2,
      status,
      plannerElementSet,
      config,
    } = node;
    const {
      label: plannerElementSetTitle,
      id: plannerElementSetId,
      type: plannerElementSetType,
      templateFields,
    } = plannerElementSet;
    const taggedInTemplateFieldsCount = _.size(templateFields);
    const configByProperty = _.keyBy(config, "globalProperty.property");

    const datasetsGroupType = getCurrentDatasetsGroupType();

    const courses = _.get(plannerElementNodeSetWrapperV2, "courses", []);
    const draftCount = _.get(
      plannerElementNodeSetWrapperV2,
      "draftData.totalCount",
      0
    );
    const publishedCount = _.get(
      plannerElementNodeSetWrapperV2,
      "publishedData.totalCount",
      0
    );

    const assessmentYearTitle =
      _.get(
        subject,
        "ibOfferedSubject.contentSubject.assessmentYearTitle",
        ""
      ) || "";

    const taggedInCoursesCount = _.size(courses);
    const { isArchived: subjectArchived = false } = subject || {};

    const data = {
      id,
      isArchived: isArchived || subjectArchived,
    };

    const name =
      datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS
        ? title
        : plannerElementSetTitle;

    const uiTypeLabels = getUiTypeLabels(t);

    _.forEach(currentColumns, columnId => {
      switch (columnId) {
        case FEEDLIST_COLUMN_IDS.NAME:
          data[columnId] = {
            text: name,
            subText: assessmentYearTitle,
          };
          break;

        case FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES:
          data[columnId] = { count: taggedInCoursesCount, name };
          break;

        case FEEDLIST_COLUMN_IDS.STATUS:
          data[columnId] = status;
          break;

        case FEEDLIST_COLUMN_IDS.LAST_UPDATED_BY:
          data[columnId] = {
            updatedBy,
            updatedAt,
          };
          break;

        case FEEDLIST_COLUMN_IDS.ACTIONS: {
          data[columnId] = {
            metaData: {
              title: name,
              plannerElementSetId,
              plannerElementNodeSetId: id,
              plannerElementSetType,
              id,
              isArchived,
            },
            options: getCurrentRowActionOptions({
              t,
              isArchived,
              taggedInCoursesCount,
              taggedInTemplateFieldsCount,
              academicSetupType,
              plannerElementType: plannerElementSetType,
              isPrimaryCurriculum,
            }),
          };
          break;
        }

        case FEEDLIST_COLUMN_IDS.COUNT:
          data[columnId] =
            status === STATUS_FILTERS.PUBLISHED ? publishedCount : draftCount;
          break;

        case FEEDLIST_COLUMN_IDS.DATASET_TYPE:
          data[columnId] = {
            text: getIsCustomPlannerElementSetType(plannerElementSetType)
              ? t("common:custom")
              : t("scopeAndSequence:toddle_managed"),
          };
          break;

        case FEEDLIST_COLUMN_IDS.UI_TYPE_FOR_UNITS:
          data[columnId] = {
            text: uiTypeLabels[configByProperty.uiType?.value] || "-",
          };
          break;

        default:
          break;
      }
    });

    return data;
  });
};

const getLabelWithCount = getMemoizedLangFunction(({ t, label, count }) =>
  t("common:label_with_count_parenthesis", {
    label,
    count,
  })
);

export const getFeedDataAndColumnList = params => {
  const {
    data,
    t,
    totalCount,
    showLoader,
    academicSetupType,
    isSubjectGroupingAllowed,
    isPrimaryCurriculum,
  } = params;
  const nodes = _.map(data?.plannerElementNodeSets?.edges, "node");
  const datasetsGroupType = getCurrentDatasetsGroupType();

  const columnListMap = FEEDLIST_COLUMN_LIST({ t, isSubjectGroupingAllowed });
  const columnOrder = _.chain(
    FEEDLIST_COLUMN_LIST_BY_GRADING_TYPE[academicSetupType]
  )
    .filter(columnId => {
      const columnConfig = FEEDLIST_COLUMN_LIST_CONFIG[columnId] || {};
      const { getIsVisible, datasetsGroupTypes } = columnConfig;
      if (getIsVisible)
        return getIsVisible({
          datasetsGroupType,
          academicSetupType,
        });
      return _.includes(datasetsGroupTypes, datasetsGroupType);
    })
    .value();
  const columnList = _.map(columnOrder, columnId => columnListMap[columnId]);
  let feedData = [];

  if (isSubjectGroupingAllowed) {
    // Group subjects by subject group
    const groupedSubjects = _.groupBy(nodes, edge =>
      _.get(edge, "subject.subjectGroup.id")
    );

    // Create sorted parent groups
    const sortedSubjectGroups = _.sortBy(
      _.map(_.entries(groupedSubjects), ([id, group]) => {
        const firstNode = _.get(group, "[0]");
        return {
          id,
          name: _.get(firstNode, "subject.subjectGroup.name", ""),
          displaySequence: _.get(
            firstNode,
            "subject.subjectGroup.displaySequence",
            0
          ),
          subjectCount: _.size(group),
        };
      }),
      "displaySequence"
    );

    // Build feed data with grouping
    feedData = _.flatMap(sortedSubjectGroups, parent => {
      const subjectGroupLabel = getLabelWithCount({
        label: parent?.name,
        count: parent?.subjectCount,
      });

      return [
        {
          id: parent.id,
          SUBJECT_NAME: {
            text: subjectGroupLabel,
          },
          isGrouped: true,
          columnId: FEEDLIST_COLUMN_IDS.NAME,
          value: <span className="text-body">{subjectGroupLabel}</span>,
          colSpan: columnList.length,
        },
        ...getRowFeedData({
          t,
          nodes: groupedSubjects[parent.id],
          currentColumns: columnOrder,
          academicSetupType,
          isPrimaryCurriculum,
        }),
      ];
    });
  } else {
    feedData = getRowFeedData({
      t,
      nodes,
      currentColumns: columnOrder,
      academicSetupType,
      isPrimaryCurriculum,
    });
  }
  return {
    columnList,
    feedData: showLoader ? _.times(totalCount, id => ({ id })) : feedData,
  };
};

export const getSettingValueForDatasetsFilters = ({
  dashboardName = DATASETS_GROUP_TYPES.STANDARDS,
} = {}) => {
  const setting = getSettingValue({
    name: DATASETS_FILTERS_SETTING,
  });
  return _.get(setting, dashboardName, EMPTY_OBJ);
};

export const getDatasetsOrderByArgs = ({
  dashboardName = DATASETS_GROUP_TYPES.STANDARDS,
} = {}) => {
  const { orderByEntityDetails } = getSettingValueForDatasetsFilters({
    dashboardName,
  });
  const { entity, direction } = orderByEntityDetails || {};
  return { orderBy: entity, orderByDirection: direction };
};

export const getNodeSetConfigFilters = () => {
  const state = store?.getState();
  const academicYearId = getSelectedAcademicYearId(state);
  const properties = ["uiType"];
  return { academicYearId, properties };
};

const DEFAULT_DATASETS_QUERY_FILTERS = {
  type: [DATASET_TYPE_FILTERS.TODDLE_MANAGED, DATASET_TYPE_FILTERS.CUSTOM],
};

export const getVariablesForDatasetsQuery = ({
  filters = DEFAULT_DATASETS_QUERY_FILTERS,
  orderByEntityDetails,
  curriculumProgram,
}) => {
  const { searchText, status, type } = filters;
  const {
    currentCurriculumProgramId,
    currentCurriculumProgramType,
    curriculumProgramType,
  } = curriculumProgram;
  const datasetsGroupType = getCurrentDatasetsGroupType();
  const types =
    CURRICULUM_PROGRAM_TO_DATASET_GROUPS_MAP[curriculumProgramType][
      datasetsGroupType
    ];
  const isStandards = datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS;
  const variables = {
    id: currentCurriculumProgramId,
    type: currentCurriculumProgramType,
    filters: {
      types: _.slice(types, 0, 1),
      searchText,
      status,
    },
    nodeSetConfigFilters: getNodeSetConfigFilters(),
    orderBy: orderByEntityDetails ? orderByEntityDetails.entity : undefined,
    orderByDirection: orderByEntityDetails
      ? orderByEntityDetails.direction
      : undefined,
  };
  if (!isStandards && ACLStore.can(MERGE_CUSTOM_DATASETS_INTO_LEARNING_GOALS)) {
    const selectedTypes = [];
    if (_.isEmpty(type)) {
      selectedTypes.push(...types, ...CUSTOM_PLANNER_ELEMENT_SET_TYPES);
    } else {
      if (_.includes(type, DATASET_TYPE_FILTERS.TODDLE_MANAGED)) {
        selectedTypes.push(...types);
      }
      if (_.includes(type, DATASET_TYPE_FILTERS.CUSTOM)) {
        selectedTypes.push(...CUSTOM_PLANNER_ELEMENT_SET_TYPES);
      }
    }
    variables.filters.types = selectedTypes;
  }

  return variables;
};

/**
 * Extracts the color name from a CSS color variable
 * @param {string} colorVariable - CSS variable string (e.g., "var(--violet-500)"", "var(--red-500))"
 * @returns {string|null} - The extracted color name or null if no match found
 * @example
 * getColorNameFromColorVariable("var(--violet-500)") // returns "violet"
 */
export const getColorNameFromColorVariable = colorVariable =>
  colorVariable?.match(/--([a-z]+)/i)?.[1] || null;

export const ICON_KIND = {
  EMOJI: "emoji",
  PRESET_ICON: "preset_icon",
  IMAGE: "image",
};

export const ICON_KIND_LABELS = t => ({
  [ICON_KIND.EMOJI]: t("common:emoji"),
  [ICON_KIND.PRESET_ICON]: t("common:icon"),
  [ICON_KIND.IMAGE]: t("common:image"),
});

/**
 * Reads the discriminator from an Attachment representing an icon/emoji/image
 * and returns it alongside boolean flags so callers don't repeat equality checks.
 * if kind is not found in metadata it considers its kind as presetIcon, as those are old data, when kind was there
 * and mostly the iconName are of dueToneIcons, which comes under presetIcon
 */
export const getIconKind = attachment => {
  const raw = _.get(attachment, "metadata.kind");
  const url = _.get(attachment, "url", null);
  const kind = _.includes(_.values(ICON_KIND), raw) ? raw : null;
  const isOldData = kind === null;
  const isPresetIcon =
    kind === ICON_KIND.PRESET_ICON || (isOldData && url === null);
  return {
    kind,
    isEmoji: kind === ICON_KIND.EMOJI,
    isPresetIcon,
    isIcon: isPresetIcon,
    isImage: kind === ICON_KIND.IMAGE || !!(isOldData && url),
  };
};

// Reads a nodeSetConfig property's draftValue. Returns true/false for "true"/"false", the raw value for other types, or undefined when the config entry is not available (caller should fall back to default behaviour).
export const getNodeSetConfig = (nodeSetConfig, property) => {
  const entry = _.get(nodeSetConfig, property);
  if (!entry) return undefined;
  const raw = entry.draftValue;
  if (raw === "true") return true;
  if (raw === "false") return false;
  return raw;
};
